/**
 * Spinner Component
 *
 * A loading indicator spinner with customizable size.
 * Component follows Smoothie UI architecture.
 *
 * @module @smoothie-framework/ui/components
 */
import { createNodeId } from '../../render/node-id';
import { mapColorTokenPath } from '@smoothie-framework/ui-core';
import { tokenPathToCssVar } from '@smoothie-framework/ui-core/resolve';
import { buildComponentClassNames, resolveResponsivePropValue } from '../../authoring/internal';
import { mergeStyleStrings } from '../../internal/style';
import { getThemeCssPrefix } from '../../theme/theme_context';
/**
 * Spinner component - loading indicator
 *
 * @example
 * ```tsx
 * <Spinner size="md" />
 *
 * <Spinner size="sm" label="Loading..." />
 *
 * <Button loading>
 *   <Spinner size="sm" />
 *   Submitting...
 * </Button>
 * ```
 */
export function Spinner(props) {
    const { id, size = 'md', label = 'Loading...', color, trackColor, borderWidth, animationDuration, className, style, 'data-ui': dataUi, } = props;
    const resolvedSize = resolveResponsivePropValue(size, 'md');
    const baseClass = 'smth-spinner';
    const mergedStyle = mergeStyleStrings(buildSpinnerCssVarStyle({ color, trackColor, borderWidth, animationDuration }), style);
    const spinnerProps = {
        className: buildComponentClassNames(baseClass, { size }, className, { fallbackVariants: { size: 'md' } }),
        'data-ui': dataUi || 'Spinner',
        'data-size': resolvedSize,
        'data-color': color ?? 'surfaceInverse',
        'data-track-color': trackColor ?? 'surfaceInverseAlpha32',
        role: 'status',
        'aria-label': label,
        'aria-live': 'polite',
        ...(id && { id }),
        ...(mergedStyle && { style: mergedStyle }),
    };
    return {
        id: createNodeId(),
        type: 'Spinner',
        props: spinnerProps,
        children: [],
        text: undefined,
    };
}
/**
 * Default props for Spinner component
 */
export const defaultSpinnerProps = {
    size: 'md',
    label: 'Loading...',
    color: 'surfaceInverse',
    trackColor: 'surfaceInverseAlpha32',
    animationDuration: '800ms',
};
function buildSpinnerCssVarStyle(options) {
    const parts = [];
    const colorValue = toSpinnerColorCssValue(options.color);
    if (colorValue !== undefined) {
        parts.push(`--smth-spinner-color: ${colorValue}`);
    }
    const trackColorValue = toSpinnerColorCssValue(options.trackColor);
    if (trackColorValue !== undefined) {
        parts.push(`--smth-spinner-track-color: ${trackColorValue}`);
    }
    const borderWidthValue = toSpinnerLengthCssValue(options.borderWidth);
    if (borderWidthValue !== undefined) {
        parts.push(`--smth-spinner-border-width: ${borderWidthValue}`);
    }
    if (typeof options.animationDuration === 'string' && options.animationDuration.trim().length > 0) {
        parts.push(`--smth-spinner-animation-duration: ${options.animationDuration.trim()}`);
    }
    return parts;
}
function toSpinnerColorCssValue(value) {
    if (typeof value !== 'string') {
        return undefined;
    }
    const trimmedValue = value.trim();
    if (trimmedValue.length === 0) {
        return undefined;
    }
    const cssPrefix = getThemeCssPrefix();
    if (/^[a-z]+\.\d+$/i.test(trimmedValue)) {
        return `var(${tokenPathToCssVar(`colors.${mapColorTokenPath(trimmedValue)}`, cssPrefix)})`;
    }
    if (/^[a-z][a-zA-Z0-9-]*$/.test(trimmedValue)) {
        return `var(${tokenPathToCssVar(`colors.${trimmedValue}`, cssPrefix)})`;
    }
    return undefined;
}
function toSpinnerLengthCssValue(value) {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return `${value}px`;
    }
    if (typeof value === 'string' && value.trim().length > 0) {
        return value.trim();
    }
    return undefined;
}
//# sourceMappingURL=Spinner.js.map